import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, Loader2, MapPin, ArrowUpRight } from "lucide-react";
import { useTranslation } from "@/i18n/LanguageContext";
import AccountShell from "@/components/vangard/AccountShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountDirectoryPage() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("");
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (industry) params.industry = industry;
      axios
        .get(`${API}/directory`, { params })
        .then((r) => { setData(r.data); setLocked(false); })
        .catch((e) => { if (e?.response?.status === 403) setLocked(true); setData({ members: [], industries: [] }); });
    }, 300);
    return () => clearTimeout(id);
  }, [q, industry]);

  const industries = useMemo(() => data?.industries || [], [data]);
  const members = data?.members || [];

  if (locked) {
    return (
      <AccountShell active="/account/directory" title={t("vc.acct.nav.directory")}>
        <div className="rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/80 p-7" data-testid="directory-locked">
          <div className="text-base">{t("vc.acct.dir.locked-title")}</div>
          <p className="text-sm text-neutral-500 font-light mt-2 max-w-md leading-relaxed">{t("vc.acct.dir.locked-body")}</p>
          <Link to="/onboarding" className="mt-5 inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 rounded-full text-[10px] tracking-[0.25em] uppercase hover:bg-white/5 transition-colors">
            {t("vc.acct.view-app")}
          </Link>
        </div>
      </AccountShell>
    );
  }

  return (
    <AccountShell active="/account/directory" title={t("vc.acct.nav.directory")}>
      <div data-testid="account-directory-page">
        <p className="text-sm text-neutral-400 font-light max-w-lg">{t("vc.acct.dir.sub")}</p>

        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={13} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("vc.acct.dir.search-ph")}
              data-testid="directory-search"
              className="w-full pl-9 pr-3 py-2.5 bg-transparent border border-white/[0.12] rounded-full focus:border-white/45 outline-none text-[13px] font-light transition-colors placeholder:text-neutral-600"
            />
          </div>
          <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 self-center" data-testid="directory-count">
            {members.length} {t("vc.acct.dir.members")}
          </span>
        </div>

        {industries.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5" data-testid="directory-filters">
            <button
              onClick={() => setIndustry("")}
              data-testid="directory-filter-all"
              className={`text-[10px] tracking-[0.15em] uppercase px-3 py-2 rounded-full border transition-colors ${
                industry === "" ? "bg-white text-black border-white" : "border-white/[0.12] text-neutral-400 hover:border-white/35 hover:text-white"
              }`}
            >
              {t("vc.acct.dir.all")}
            </button>
            {industries.map((ind) => (
              <button
                key={ind}
                onClick={() => setIndustry(ind === industry ? "" : ind)}
                data-testid={`directory-filter-${ind.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`text-[10px] tracking-[0.15em] uppercase px-3 py-2 rounded-full border transition-colors ${
                  industry === ind ? "bg-white text-black border-white" : "border-white/[0.12] text-neutral-400 hover:border-white/35 hover:text-white"
                }`}
              >
                {ind}
              </button>
            ))}
          </div>
        )}

        {data === null ? (
          <div className="mt-10 flex items-center gap-3 text-neutral-500 text-sm">
            <Loader2 size={16} className="animate-spin" /> {t("common.loading")}
          </div>
        ) : members.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/80 p-7" data-testid="directory-empty">
            <div className="text-base">{t("vc.acct.dir.empty-title")}</div>
            <p className="text-sm text-neutral-500 font-light mt-2 max-w-md">{t("vc.acct.dir.empty-body")}</p>
          </div>
        ) : (
          <div className="mt-6 grid sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="directory-list">
            {members.map((m) => (
              <div
                key={m.user_id}
                className="group h-fit rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/80 p-5 hover:border-white/25 transition-colors"
                data-testid={`directory-card-${m.user_id}`}
              >
                <div className="flex items-start gap-3">
                  {m.picture ? (
                    <img src={m.picture} alt={`${m.name || "Member"} portrait`} loading="lazy" className="w-10 h-10 rounded-full object-cover grayscale border border-white/15" />
                  ) : (
                    <span className="w-10 h-10 rounded-full flex items-center justify-center border border-white/15 bg-[#111] text-[11px]">
                      {((m.name || "?").split(" ").map((w) => w[0]).join("")).slice(0, 2).toUpperCase()}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{m.name || "—"}</div>
                    <div className="text-[11px] text-neutral-500 truncate">
                      {[m.role, m.company].filter(Boolean).join(" · ") || m.headline || "—"}
                    </div>
                  </div>
                  <span className="text-[8px] tracking-[0.25em] uppercase text-neutral-500 border border-white/[0.12] rounded-full px-2 py-0.5 shrink-0">
                    {m.membership_tier}
                  </span>
                </div>

                {m.headline && <p className="text-[13px] text-neutral-400 font-light mt-3 leading-relaxed line-clamp-2">{m.headline}</p>}

                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {m.industry && (
                    <span className="text-[9px] tracking-[0.2em] uppercase text-neutral-400 border border-white/[0.10] rounded-full px-2.5 py-1">{m.industry}</span>
                  )}
                  {m.location && (
                    <span className="text-[9px] tracking-[0.2em] uppercase text-neutral-500 inline-flex items-center gap-1">
                      <MapPin size={9} /> {m.location}
                    </span>
                  )}
                </div>

                {m.expertise?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {m.expertise.map((e) => (
                      <span key={e} className="text-[10px] text-neutral-500 bg-white/[0.04] rounded-full px-2 py-0.5">{e}</span>
                    ))}
                  </div>
                )}

                {m.slug && (
                  <Link
                    to={`/u/${m.slug}`}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-white/[0.1] px-3.5 py-2 text-[10px] tracking-[0.25em] uppercase text-neutral-300 hover:text-white hover:border-white/30 transition-colors"
                    data-testid={`directory-open-${m.user_id}`}
                  >
                    {t("vc.acct.dir.view")} <ArrowUpRight size={11} />
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AccountShell>
  );
}
